import type { TPlan } from "@/core/environment/types";
import { track } from "@/features/analytics";
import { useUpgradePlanMutation } from "@api/accounts";
import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import Spinner from "@components/ui/spinner";
import Tabs from "@components/ui/tabs";
import { environment } from "@environment";
import { useAppDispatch, useAppSelector } from "@store";
import { getAccount, getAccountSeats } from "@store/account/selectors";
import { closeDialog } from "@store/dialogs";
import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { handleAnalytics } from "../utils";

const PLAN_INCLUDES = [
  "Remove Storydoc branding",
  "Export to PDF",
  "Collaboration",
  "Integrations",
  "Personalization with variables",
  "Folder management",
  "Analytics",
  "30 active documents",
  "Image & text AI credits",
  "Version control",
];

const UpgradeSubscriptionToPro = () => {
  const dispatch = useAppDispatch();
  const [period, setPeriod] = useState<"monthly" | "yearly">("monthly");
  const account = useAppSelector(getAccount);
  const [upgradePlan, { isLoading }] = useUpgradePlanMutation();

  const users = useAppSelector((s) => {
    const { planLimit, additionalSeats } = getAccountSeats(s);
    return Number(planLimit) + Number(additionalSeats);
  });
  const proPlans = useMemo(() => {
    const plans = environment.paddle.plans.filter(
      (plan) => plan.title === "Pro" && plan.active,
    );
    return {
      monthly: plans.find((plan) => plan.period === "Monthly") as TPlan,
      yearly: plans.find((plan) => plan.period === "Annually") as TPlan,
    } as const;
  }, []);

  const close = useCallback(() => {
    if (isLoading) return;
    dispatch(closeDialog("upgrade-subscription-to-pro"));
  }, [dispatch, isLoading]);

  const handleUpgradePlan = async () => {
    if (isLoading) return;
    const plan = proPlans[period];
    const properties = {
      "subscription title": plan.title,
      "subscription period": plan.period,
      "subscription price": plan.price,
      "is starter to pro": true,
    };
    track("click update subscription", properties);
    try {
      await upgradePlan({
        planId: proPlans[period].id,
      }).unwrap();
      close();
      toast.success("Plan upgraded successfully");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      handleAnalytics({ plan: plan, account });
    } catch (error) {
      toast.error("Failed to upgrade plan");
      track("failed upgrade customer", {
        ...properties,
        "error message": error instanceof Error ? error.message : String(error),
      });
      console.error(error);
    }
  };

  return (
    <div className="tw-relative tw-flex tw-w-[474px] tw-flex-col tw-items-center tw-rounded-lg tw-bg-blue tw-p-4 tw-shadow-main">
      <div
        onClick={close}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") close();
        }}
        role="button"
        tabIndex={0}
        className="tw-absolute tw-left-full tw-top-0 tw-flex tw-size-8 tw-translate-x-2 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-shadow-main tw-transition-opacity hover:tw-opacity-80"
      >
        <Icon icon="ri-close-fill" className="tw-text-xl tw-text-blue-700" />
      </div>
      <div className="tw-mb-4 tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-pro-base">
        <Icon icon="ri-vip-crown-fill" className="tw-text-xl tw-text-white" />
      </div>
      <p className="tw-mb-2 tw-mt-0 tw-font-sans tw-text-xl tw-font-bold tw-text-white">
        Upgrade your account to{" "}
        <strong className="tw-font-bold tw-text-pro-base">Pro</strong>
      </p>
      <p className="tw-mb-6 tw-mt-0 tw-text-center tw-text-[13px] tw-font-semibold tw-text-white">
        You'll be charged for the starter-pro plan <br /> price difference.
      </p>
      <div className="tw-mx-auto tw-w-80 tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white tw-px-5 tw-py-4 tw-shadow-main">
        <Tabs
          defaultValue="monthly"
          className="tw-mb-6"
          onValueChange={(value) => setPeriod(value as "monthly" | "yearly")}
        >
          <Tabs.Option value="monthly" className="tw-h-8 tw-text-sm">
            Monthly
          </Tabs.Option>
          <Tabs.Option value="yearly" className="tw-h-8 tw-text-sm">
            Yearly
          </Tabs.Option>
        </Tabs>
        <div className="tw-mb-2 tw-flex tw-items-end tw-justify-center">
          <span className="tw-relative tw-font-sans tw-text-[35px]/none tw-font-extrabold tw-text-primary">
            ${proPlans[period].price / (period === "yearly" ? 12 : 1)}
            {period === "yearly" && (
              <span className="tw-absolute tw-left-full tw-top-0 -tw-translate-y-[2px] tw-translate-x-[2px] tw-font-sans tw-text-base/none tw-text-red-600 tw-line-through">
                ${proPlans.monthly.price}
              </span>
            )}
          </span>
          <span className="tw-ml-1 tw-font-sans tw-text-base tw-font-medium tw-text-blue-500">
            {" "}
            / user per month
          </span>
        </div>
        <div className="tw-mb-6 tw-flex tw-items-center tw-justify-center">
          <span className="tw-mr-2 tw-flex tw-h-5 tw-items-center tw-rounded tw-bg-blue-100 tw-px-2 tw-text-[10px] tw-text-blue-700">
            {users} {users === 1 ? "user" : "users"}
          </span>
          <span className="tw-flex tw-h-5 tw-items-center tw-rounded tw-bg-blue-100 tw-px-2 tw-text-[10px] tw-text-blue-700">
            ${proPlans[period].price} paid {period} (tax not included)
          </span>
        </div>
        <div className="tw-space-y-1">
          {PLAN_INCLUDES.map((item) => (
            <div
              key={item}
              className="tw-flex tw-items-center tw-font-sans tw-text-[11px] tw-text-blue"
            >
              <Icon
                icon="ri-checkbox-circle-fill"
                className="tw-mr-2 tw-text-sm tw-text-green-500"
              />
              {item}
            </div>
          ))}
        </div>
      </div>
      <Button
        variant="pro"
        className="tw-mt-6 tw-w-80"
        onClick={handleUpgradePlan}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
            Upgrading...
          </>
        ) : (
          "Upgrade now"
        )}
      </Button>
    </div>
  );
};

export default UpgradeSubscriptionToPro;
